export type EcommerceContextType =
  | "ecommerce_product"
  | "ecommerce_metrics"
  | "ecommerce_ad_cost"
  | "ecommerce_strategy"
  | "ecommerce_portfolio";

export type VideoEditorContextType = "video_editor_project";

export type WidgetContextType = EcommerceContextType | VideoEditorContextType;

export interface WidgetContext {
  type: WidgetContextType;
  capturedAt: number;
  widgetId: string;
  stepIndex: number;
  label: string;
  data: Record<string, unknown>;
}

export interface WidgetContextExtractorConfig {
  type: WidgetContextType;
  label: string;
  extract: () => { type: WidgetContextType; data: Record<string, unknown> };
  /** Fires after the context is attached. Lets a widget run its normal click
   *  behavior (e.g. opening a detail modal) even though pick-mode swallows the
   *  native click event. */
  onExtracted?: () => void;
}

export interface RegisteredExtractor {
  contextId: string;
  config: WidgetContextExtractorConfig;
}

/** Minimum pipeline step that must be completed for a context type to be extractable. */
export const CONTEXT_TYPE_MIN_STEP: Record<string, number> = {
  // `ecommerce_product` is intentionally unlisted: products render on the ads
  // workspace before any pipeline step advances, and extractable product
  // widgets only mount when their data is already available — so no step gate
  // is needed.
  ecommerce_metrics: 2,
  ecommerce_ad_cost: 2,
  ecommerce_strategy: 2,
  ecommerce_portfolio: 2,
};

type StepStatusValue = "pending" | "running" | "waiting" | "completed" | "error" | "failed";

export function isStepReady(
  contextType: string,
  stepStatuses: Record<number, StepStatusValue>,
): boolean {
  const minStep = CONTEXT_TYPE_MIN_STEP[contextType];
  if (minStep === undefined) return true;
  const status = stepStatuses[minStep];
  // Allow extraction whenever the step has progressed past "pending".
  // The widget is only rendered when its data exists, so any non-pending
  // state (running, waiting, completed, error, failed) is safe.
  return !!status && status !== "pending";
}
